import { selectRiskConfig } from '@Selectors/Risk.selectors';
import { useIsFeatureEnabled } from '@innovyze/stylovyze';
import useGetWeightedScores from './useGetWeightedScores';
import { SetStateAction, useEffect, useState } from 'react';
import { useNoConditionData } from '@Hooks/useNoConditionData';
import { useRiskDetailsSelectors } from './useRiskDetailsSelectors';
import { useKeyedLocalStorage } from './useKeyedLocalStorage';
import { useResultsGridFlags } from './useResultsGridFlags';
import {
	reconcileColumnOrder,
	redesignDefaultOrder,
} from './columnOrder.utils';

function useLegacyColumnOrder() {
	const riskConfig = selectRiskConfig();
	const [cofFilled, setCofFilled] = useState(false);
	const [lofFilled, setLofFilled] = useState(false);

	const weightedScores = useGetWeightedScores();

	const weightedCofColumnNames = weightedScores.cofScores?.map(
		cof => cof.field,
	);

	const weightedLofColumnNames = weightedScores.lofScores.map(
		lof => lof.field,
	);

	const isIndividualScoresEnabled = useIsFeatureEnabled(
		'info-360-risk-datagrid-individual-scores',
	);

	const noConditionData = useNoConditionData(
		riskConfig.riskSetting.assetType,
	);

	const [columnOrder, setColumnOrder] = useState<string[]>([
		'ASSET_ID',
		'RISK_CHANGE',
		'CURRENT_RISK',
		'CURRENT_COF',
		'CURRENT_LOF',
		...(!noConditionData ? ['CONDITION_DATA'] : []),
		'GRADE',
		'Menu',
	]);

	useEffect(() => {
		setColumnOrder(co => {
			if (noConditionData) {
				return co.filter(col => col != 'CONDITION_DATA');
			} else {
				if (!co.includes('CONDITION_DATA')) {
					const lofIndex = co.findIndex(col => col === 'CURRENT_LOF');
					if (lofIndex !== -1) {
						const newOrder = [...co];
						newOrder.splice(lofIndex + 1, 0, 'CONDITION_DATA');
						return newOrder;
					}
					return [...co, 'CONDITION_DATA'];
				}
				return co;
			}
		});
	}, [noConditionData]);

	useEffect(() => {
		if (
			isIndividualScoresEnabled &&
			!cofFilled &&
			weightedCofColumnNames.length > 0
		) {
			setColumnOrder(co => [...co, ...weightedCofColumnNames]);
			setCofFilled(true);
		}
	}, [weightedCofColumnNames]);

	useEffect(() => {
		if (
			isIndividualScoresEnabled &&
			!lofFilled &&
			weightedLofColumnNames.length > 0
		) {
			setColumnOrder(co => [...co, ...weightedLofColumnNames]);
			setLofFilled(true);
		}
	}, [weightedLofColumnNames]);

	return { columnOrder, setColumnOrder };
}

function usePersistedColumnOrder(columnFields: string[], configId: string) {
	const { tenantId, userId } = useRiskDetailsSelectors();
	const weightedScores = useGetWeightedScores();
	// tenantId is typed unknown by the selector; only a string may build a key.
	const tenant = typeof tenantId === 'string' ? tenantId : '';
	// No key until all ids resolve, so nothing is read or written under a
	// shared half-built key.
	const [saved, setSaved] = useKeyedLocalStorage<string[]>(
		userId && tenant && configId
			? `${userId}.${tenant}.riskTable.columnOrder.${configId}`
			: null,
	);
	const defaultOrder = redesignDefaultOrder(
		weightedScores.lofScores.map(c => c.field),
		weightedScores.cofScores.map(c => c.field),
	);
	const columnOrder = reconcileColumnOrder(columnFields, defaultOrder, saved);
	const setColumnOrder = (action: SetStateAction<string[]>) =>
		setSaved(typeof action === 'function' ? action(columnOrder) : action);
	return { columnOrder, setColumnOrder };
}

export default function useRiskDetailsColumnOrder(
	columnFields: string[] = [],
	configId = '',
): {
	columnOrder: string[];
	setColumnOrder: React.Dispatch<React.SetStateAction<string[]>>;
} {
	// Both hooks always run (rules of hooks); the flag picks which result.
	const legacy = useLegacyColumnOrder();
	const persisted = usePersistedColumnOrder(columnFields, configId);
	const { isRedesign } = useResultsGridFlags();
	return isRedesign ? persisted : legacy;
}
